import React, { useCallback, useEffect } from 'react';
import { useEntityLoader } from './entity-form/model/useEntityLoader';
import EntityForm from './entity-form/ui/EntityForm';
import { isErrorResponse } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import { useTranslation } from 'react-i18next';
import { BaseEntity, BaseEntityNameLess } from '@api/entities/BaseEntity';
import { useDispatch } from 'react-redux';
import UndoLastOperationButton from '../road-network-editor/RoadNetworkManager/UndoLastOperationButton/UndoLastOperationButton';
import { setSourceReloadRequired } from '@redux/map/map-reducer';
import { useLocation } from 'react-router-dom';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { useLayersInteractivity } from '@hooks/map/useLayersInteractivity';
import { FormDataObj } from '@utils/types';
import { useNavigatePreserveSearch } from '@hooks/useNavigatePreserveSearch';
import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import {
    useConditionalBlocker,
    useNavigationBlockerContext,
} from '@context/navigationBlockerContext';
import LockedLayerNotifier from '@components/LockedLayerNotifier/LockedLayerNotifier';
import { useMapLayersClickabilityContext } from '@context/mapLayersClickability';
import { EntityFormProps, UseEntityLoaderProps } from './entity-form/types';

//TODO loadByDefault is to avoid entity load in TransitRouteEdit => update architecture
type Props = {
    entityId: string;
    entityName: string;
    layerId: string;
    onSaveFn?: (resp: any) => Promise<void> | void;
    getUrlOnSaveFn?: (resp: any) => string | undefined | Promise<string | undefined>;
    getUrlOnDeleteFn?: () => string;
    actionButtons?: JSX.Element | JSX.Element[];
    undoLastActionBtn?: boolean;
    loadByDefault?: boolean;
} & UseEntityLoaderProps &
    Pick<EntityFormProps, 'showDeleteBtn'>;

const EntityEditor = <T extends BaseEntityNameLess>({
    entityId,
    entityName,
    layerId,
    saveFnArgs,
    presetProperty,
    onSaveFn,
    getUrlOnSaveFn,
    getUrlOnDeleteFn,
    actionButtons,
    undoLastActionBtn = false,
    entityView,
    loadByDefault = true,
    showDeleteBtn,
}: Props) => {
    const {
        loading,
        initialEntity,
        entity,
        unsavedEntityChanges,
        metadata,
        loadData,
        changeForm,
        resetForm,
        saveFn,
        deleteFn,
    } = useEntityLoader<T>({ entityName, saveFnArgs, presetProperty });
    const { recoverLayersInteractivity } = useMapLayersClickabilityContext();
    const { setDrawing } = useLayersInteractivity();
    const { addToast } = useToastContext();
    const { t } = useTranslation();
    const location = useLocation();

    useConditionalBlocker({ checkFn: () => unsavedEntityChanges });
    const { blocker, proceed, reset } = useNavigationBlockerContext();

    const navigate = useNavigatePreserveSearch();
    const dispatch = useDispatch();

    const updatedEntityId = entityId === 'new' ? null : entityId;

    useEffect(() => {
        if (loadByDefault) {
            void (async function () {
                //TODO redirect to list or project if entity doesn't exist
                await loadData({ entityName, entityId: updatedEntityId, entityView });
            })();
        }
    }, [entityName, updatedEntityId, entityView, loadByDefault, loadData]);

    const saveEntity = useCallback(
        async (formDataObj: FormDataObj) => {
            const abortController = new AbortController();

            const resp = await saveFn({
                id: updatedEntityId,
                entityName,
                data: formDataObj,
                abortSignal: abortController.signal,
            });

            if (isErrorResponse(resp)) {
                addToast(`${resp.error_description}`, 'error');
            } else {
                addToast(t('common.save-successful'), 'success');
                await loadData({ entityName, entityId: updatedEntityId, entityView });
                if (layerId) {
                    dispatch(
                        setSourceReloadRequired({ sourceId: layerId, isReloadRequired: true })
                    );
                }
                setDrawing(false);
                recoverLayersInteractivity();

                await onSaveFn?.(resp);

                if (getUrlOnSaveFn) {
                    const url = await getUrlOnSaveFn?.(resp);
                    if (url) {
                        navigate(url, { state: { prevUrl: location.pathname }, relative: 'path' });
                    }
                }
            }
        },
        [
            entityName,
            saveFn,
            updatedEntityId,
            layerId,
            getUrlOnSaveFn,
            setDrawing,
            recoverLayersInteractivity,
        ]
    );

    const navigateToRequiredUrl = useCallback((getUrlOnDeleteFn: () => string) => {
        const url = getUrlOnDeleteFn();
        if (url) {
            navigate(url, { state: { prevUrl: location.pathname } });
        }
    }, []);

    const deleteEntity = useCallback(async () => {
        if (updatedEntityId) {
            const resp = await deleteFn?.(updatedEntityId);

            if (isErrorResponse(resp)) {
                addToast(`${resp.error_description}`, 'error');
            } else {
                addToast(t('common.delete-successful'), 'success');

                if (layerId) {
                    dispatch(
                        setSourceReloadRequired({ sourceId: layerId, isReloadRequired: true })
                    );
                }

                if (getUrlOnDeleteFn) {
                    navigateToRequiredUrl(getUrlOnDeleteFn);
                }
            }
        }
    }, [layerId, navigateToRequiredUrl, updatedEntityId]);

    const onLastActionUndo = useCallback(async () => {
        await loadData({ entityName, entityId: updatedEntityId });
    }, [loadData, updatedEntityId, entityName]);

    const confirmNavigation = useCallback(() => {
        proceed();
    }, [proceed]);

    const cancelNavigation = useCallback(() => {
        reset();
    }, [reset]);

    return (
        <LoadingBackdrop isVisible={loading}>
            <EntityForm
                loading={loading}
                initialEntity={initialEntity as any}
                entity={entity}
                metadata={metadata}
                changeFormFn={changeForm}
                resetFormFn={resetForm}
                saveFn={saveEntity}
                deleteModalTitle={t('common.delete-confirmation')}
                deleteFn={deleteEntity}
                icons={
                    <>
                        {actionButtons}
                        {undoLastActionBtn ? (
                            <UndoLastOperationButton onFinishFn={onLastActionUndo} />
                        ) : null}
                    </>
                }
                showDeleteBtn={showDeleteBtn}
                disabledResetBtn={!unsavedEntityChanges}
            />
            {blocker && blocker?.state === 'blocked' ? (
                <ConfirmationModal
                    title={t('common.confirm-leaving-action-with-unsaved-changes')}
                    setModalShow={cancelNavigation}
                    onConfirmFn={confirmNavigation}
                    onCancelFn={cancelNavigation}
                />
            ) : null}
            <LockedLayerNotifier
                layerId={layerId}
                toastMsg={t('entity-form.not-clickable-layer-warn-toast')}
            />
        </LoadingBackdrop>
    );
};

export default EntityEditor;
